import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { auth } from "../firebase/firebase";
import { setUser } from "../store/slices/session/sessionSlice";
import * as Sentry from "@sentry/react";

interface TokenValidationState {
  isValidating: boolean;
  isValid: boolean;
  error: string | null;
  isTokenExpired: boolean; // Only true when token is actually expired
}

/**
 * Hook to proactively validate and refresh Firebase auth tokens
 * This prevents users from seeing auth errors when returning after long periods
 */
export const useTokenValidation = () => {
  const dispatch = useDispatch();
  const [state, setState] = useState<TokenValidationState>({
    isValidating: true,
    isValid: false,
    error: null,
    isTokenExpired: false, // Will be set to true only if token is actually expired
  });

  useEffect(() => {
    const validateAndRefreshToken = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setState({
            isValidating: false,
            isValid: false,
            error: "No user logged in",
            isTokenExpired: false,
          });
          return;
        }

        // Get the current token and check if it's expired
        const token = await user.getIdToken(false);

        // Parse the token to check expiry
        const tokenPayload = JSON.parse(atob(token.split(".")[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = tokenPayload.exp - currentTime;

        // Check if token is actually expired (not just close to expiry)
        const isExpired = timeUntilExpiry <= 0;

        if (isExpired) {
          // Token is expired, show loading while refreshing
          setState({
            isValidating: true,
            isValid: false,
            error: null,
            isTokenExpired: true, // This will trigger loading screen
          });

          console.log("Token is expired, refreshing...");
          const freshToken = await user.getIdToken(true);

          // Update Redux store with fresh token
          dispatch(
            setUser({
              uid: user.uid,
              email: user.email || "",
              token: freshToken,
              creationTime: user.metadata.creationTime || "",
              lastSignInTime: user.metadata.lastSignInTime || "",
            })
          );
        } else {
          // Token is still valid, just update store (no loading screen)
          dispatch(
            setUser({
              uid: user.uid,
              email: user.email || "",
              token,
              creationTime: user.metadata.creationTime || "",
              lastSignInTime: user.metadata.lastSignInTime || "",
            })
          );
        }

        setState({
          isValidating: false,
          isValid: true,
          error: null,
          isTokenExpired: false, // Token is valid, not expired
        });
      } catch (error) {
        console.error("Token validation failed:", error);
        Sentry.captureException(error);
        setState({
          isValidating: false,
          isValid: false,
          error:
            error instanceof Error ? error.message : "Token validation failed",
          isTokenExpired: true, // Mark as expired if validation fails
        });
      }
    };

    validateAndRefreshToken();
  }, [dispatch]);

  return state;
};
